/**
 * Field Map — what this deal holds, how it got there, and what is missing.
 *
 * The dossier's last tab, drawn rather than written. One sentence says where
 * the deal stands; a row of tiles carries the scores; a stacked bar per dossier
 * page shows, for every catalog field, whether the dossier holds it and — when
 * it does not — the one reason it is blank; a ranked bar chart shows which
 * missing input would fill the most calculated fields; and the table beneath
 * lists every field with its status, so the chart's question ("which ones?") is
 * always one click away.
 *
 * Nothing is invented here. The server composes the platform's own completeness
 * measure, the package search, the field events and the derivation rules; this
 * page only groups and lays them out (see fieldMapModel.ts). The scores are
 * counts of fields, never weighted: a weighted grade would hide the one thing
 * this page is for — which specific field is missing and whose move it is. A
 * package that was not searched is never reported as lacking a value.
 *
 * Layout: the page fills its container. The tiles and the three charts take a
 * height their content needs; the field table and the worklist take the rest and
 * scroll inside their panels, so no panel ends in an empty band. Below the xl
 * breakpoint the panels stack and the page scrolls as a whole; at xl it does too
 * when the window is too short for every panel's minimum.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronRight, Loader2, RefreshCw } from 'lucide-react'

import { loansService, type FieldMapRow } from '@/services/loans'
import { LOAN_QUERY_KEYS } from '@/services/loanQueryKeys'
import { DossierPanel, EmptyLine } from './ui'
import {
  BUCKET_ORDER,
  BUCKETS,
  bucketOf,
  countBuckets,
  headline,
  holdsBack,
  matchesStatus,
  pageBars,
  rowReason,
  sortFields,
  unlockRanking,
  type SortKey,
  type StatusFilter,
} from './fieldMapModel'
import { Banner, Chip, FieldDetail, Headline, HowToRead, StatusChip } from './FieldMapParts'
import { ExtractionPanel, PagesPanel, ScoreTiles, UnlockPanel, WorklistPanel } from './FieldMapPanels'

export function FieldMapPage({ loanId, demo = false }: { loanId: string; demo?: boolean }) {
  const [selected, setSelected] = useState<string | null>(null)
  const [pageFilter, setPageFilter] = useState('')
  const [status, setStatus] = useState<StatusFilter>('')
  const [q, setQ] = useState('')
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 } | null>(null)
  /** Opened from a chart or the worklist (so bring it to the top of the table), not toggled in the table. */
  const [reveal, setReveal] = useState(false)

  const map = useQuery({
    queryKey: LOAN_QUERY_KEYS.fieldMap(loanId),
    queryFn: () => loansService.getFieldMap(loanId, true),
    enabled: Boolean(loanId) && !demo,
    staleTime: 60_000,
  })
  const data = map.data

  const model = useMemo(() => {
    if (!data) return null
    const counts = countBuckets(data.fields)
    return { counts, ...pageBars(data), unlock: unlockRanking(data.fields), line: headline(data, counts) }
  }, [data])

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const kept = (data?.fields ?? []).filter(
      (r) =>
        (!pageFilter || r.page === pageFilter) &&
        matchesStatus(r, status) &&
        (!needle || `${r.label} ${r.field} ${r.group}`.toLowerCase().includes(needle)),
    )
    return sortFields(kept, sort?.key ?? null, sort?.dir ?? 1)
  }, [data, pageFilter, status, q, sort])

  if (demo) {
    return <EmptyLine>The field map is not available for demo deals: there is no real package to score.</EmptyLine>
  }
  if (map.isLoading) {
    return (
      <div className="flex h-full min-h-[12rem] items-center justify-center gap-2 text-[12px] text-stone-500" role="status">
        <Loader2 size={14} className="animate-spin" aria-hidden /> Reading the package and scoring the deal…
      </div>
    )
  }
  if (map.isError || !data || !model) {
    const code = (map.error as { response?: { status?: number } } | null)?.response?.status
    return (
      <div className="flex h-full min-h-[12rem] flex-col items-center justify-center gap-2 text-[12px] text-stone-400" role="alert">
        <span>{code === 403 ? 'Your role cannot view this deal’s field map.' : 'The field map could not be loaded.'}</span>
        {code === 403 ? null : (
          <button type="button" onClick={() => map.refetch()} className="rounded border border-white/10 px-2 py-1 text-[11px] text-stone-300 hover:text-stone-100">
            Try again
          </button>
        )}
      </div>
    )
  }

  const empty = data.fields.length === 0

  /** Jump to one field: clear every filter so it is certain to be in the table. */
  const open = (field: string) => {
    setReveal(true)
    setSelected(field)
    setPageFilter('')
    setStatus('')
    setQ('')
  }
  /** Each column sorts its natural way first, then the other way, then back to the catalog's order. */
  const toggleSort = (key: SortKey) => {
    const first = key === 'holds' ? -1 : 1
    setSort((cur) => (cur?.key !== key ? { key, dir: first } : cur.dir === first ? { key, dir: first === 1 ? -1 : 1 } : null))
  }
  const unlockMax = Math.max(1, ...model.unlock.map((u) => u.total))

  return (
    <div className="h-full min-h-0 overflow-y-auto overflow-x-hidden pr-0.5" data-fieldmap-page>
      <div
        className={clsx(
          'flex flex-col gap-2',
          empty ? 'h-full' : 'min-h-full xl:grid xl:h-full xl:min-h-0 xl:grid-rows-[auto_auto_minmax(14.5rem,1fr)]',
        )}
      >
        <div className={clsx('flex flex-col gap-2', empty ? 'min-h-0 flex-1' : 'shrink-0')}>
          <header className="flex shrink-0 items-start justify-between gap-3">
            <h2 className="sr-only">Field Map</h2>
            <Headline text={model.line} />
            <div className="flex shrink-0 items-center gap-1.5">
              <HowToRead basis={data.basis} />
              <button
                type="button"
                onClick={() => map.refetch()}
                disabled={map.isFetching}
                className="inline-flex items-center justify-center gap-1.5 rounded border border-white/10 px-2.5 py-1.5 text-[10px] uppercase tracking-wider text-stone-400 hover:text-stone-200 disabled:opacity-50"
                title="Re-read the package and recount"
              >
                <RefreshCw size={11} className={map.isFetching ? 'animate-spin' : undefined} aria-hidden /> Re-check
              </button>
            </div>
          </header>

          {!data.hasPackage ? (
            <Banner tone="stone">No intake package is attached to this deal, so blank fields are entry gaps, not extraction gaps.</Banner>
          ) : null}
          {data.hasPackage && !data.searched ? (
            <Banner>The package was not searched, so a blank is not reported as missing from it.{data.searchError ? ` ${data.searchError}` : ''}</Banner>
          ) : null}
          {data.sourcesUnreadable > 0 ? (
            <Banner>{data.sourcesUnreadable} stored source{data.sourcesUnreadable === 1 ? '' : 's'} could not be opened. Re-upload the package so it can be searched.</Banner>
          ) : null}
          {data.findingsWithheld ? (
            <Banner tone="stone">Values found in the package are hidden: your role has no intake access. Counts and reasons are shown.</Banner>
          ) : null}

          {empty ? (
            <div className="flex min-h-[10rem] flex-1 items-center justify-center rounded-lg border border-amber-400/15 bg-black/30 text-[12px] text-stone-500">
              No catalog field came back for this deal, so there is nothing to chart. Re-check reads the package again.
            </div>
          ) : (
            <ScoreTiles data={data} counts={model.counts} />
          )}
        </div>

        {empty ? null : (
          <>
            <div className="grid shrink-0 gap-2 xl:h-[clamp(14rem,24vh,18rem)] xl:grid-cols-12 xl:grid-rows-[minmax(0,1fr)]">
              <div className="min-h-0 min-w-0 max-xl:min-h-[14rem] xl:col-span-5">
                <PagesPanel pages={model.pages} all={model.all} status={status} onStatus={setStatus} pageFilter={pageFilter} onPage={setPageFilter} />
              </div>
              <div className="min-h-0 min-w-0 max-xl:min-h-[14rem] xl:col-span-4">
                <UnlockPanel entries={model.unlock} onOpen={open} />
              </div>
              <div className="min-h-0 min-w-0 max-xl:min-h-[14rem] xl:col-span-3">
                <ExtractionPanel data={data} />
              </div>
            </div>

            <div className="grid flex-1 gap-2 xl:min-h-0 xl:grid-cols-12 xl:grid-rows-[minmax(0,1fr)]">
              <div className="min-h-0 min-w-0 max-xl:h-[30rem] xl:col-span-7">
                <DossierPanel
                  title="Every dossier field"
                  dense
                  scroll={false}
                  headerExtra={
                    <span className="font-mono text-[10px] tabular-nums text-stone-500">{rows.length} of {data.fields.length} fields</span>
                  }
                >
                  <div className="flex h-full min-h-0 flex-col gap-1.5">
                    <div className="flex shrink-0 flex-wrap items-center gap-1.5 px-1 pt-1 text-[11px]">
                      <select aria-label="Dossier page" value={pageFilter} onChange={(e) => setPageFilter(e.target.value)} className="rounded border border-white/10 bg-black px-2 py-1 text-stone-300">
                        <option value="">All pages</option>
                        {data.score.byPage.map((p) => <option key={p.page}>{p.page}</option>)}
                      </select>
                      <select aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value as StatusFilter)} className="rounded border border-white/10 bg-black px-2 py-1 text-stone-300">
                        <option value="">All statuses</option>
                        <option value="blank">All blank ({data.fields.length - model.counts.shown})</option>
                        {BUCKET_ORDER.map((id) => <option key={id} value={id}>{BUCKETS[id].label} ({model.counts[id]})</option>)}
                      </select>
                      <input
                        type="search"
                        aria-label="Search fields"
                        placeholder="Search fields"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="min-w-0 flex-1 rounded border border-white/10 bg-black px-2 py-1 text-stone-200 placeholder:text-stone-600 sm:max-w-[14rem]"
                      />
                    </div>
                    <div data-table-scroll className="min-h-0 flex-1 overflow-auto rounded border border-white/[0.07]">
                      <table aria-label="Dossier fields" className="w-full table-fixed border-collapse text-left text-[11px]">
                        <colgroup>
                          <col />
                          <col className="w-[4.75rem] max-md:hidden" />
                          <col className="w-[8.5rem]" />
                          <col className="max-lg:hidden" />
                          <col className="w-[5.5rem] max-lg:hidden" />
                          <col className="w-[5rem] max-lg:hidden" />
                        </colgroup>
                        <thead className="sticky top-0 z-10 bg-stone-950 font-mono text-[9px] uppercase tracking-wider text-stone-500">
                          <tr>
                            <SortTh label="Field" k="label" sort={sort} onSort={toggleSort} />
                            <SortTh label="Page" k="page" sort={sort} onSort={toggleSort} className="max-md:hidden" />
                            <SortTh label="Status" k="status" sort={sort} onSort={toggleSort} />
                            <th scope="col" className="px-2 py-1.5 text-left font-medium max-lg:hidden">Origin or reason</th>
                            <SortTh label="Holds back" k="holds" sort={sort} onSort={toggleSort} className="max-lg:hidden" />
                            <th scope="col" className="px-2 py-1.5 text-left font-medium max-lg:hidden">Flags</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r) => (
                            <FieldRow
                              key={r.field}
                              row={r}
                              open={selected === r.field}
                              reveal={reveal}
                              maxHolds={unlockMax}
                              onToggle={() => {
                                setReveal(false)
                                setSelected(selected === r.field ? null : r.field)
                              }}
                            />
                          ))}
                        </tbody>
                      </table>
                      {rows.length === 0 ? <EmptyLine>No field matches these filters.</EmptyLine> : null}
                    </div>
                  </div>
                </DossierPanel>
              </div>
              <div className="min-h-0 min-w-0 max-xl:h-[30rem] xl:col-span-5">
                <WorklistPanel data={data} onOpen={open} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function SortTh({
  label,
  k,
  sort,
  onSort,
  className,
}: {
  label: string
  k: SortKey
  sort: { key: SortKey; dir: 1 | -1 } | null
  onSort: (k: SortKey) => void
  className?: string
}) {
  const on = sort?.key === k
  const Icon = on ? (sort.dir === 1 ? ArrowUp : ArrowDown) : ArrowUpDown
  return (
    <th scope="col" aria-sort={on ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'} className={clsx('px-2 py-1.5 text-left font-medium', className)}>
      <button
        type="button"
        onClick={() => onSort(k)}
        className={clsx(
          'inline-flex items-center gap-1 uppercase tracking-wider hover:text-stone-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300',
          on && 'text-amber-300',
        )}
      >
        {label}
        <Icon size={9} aria-hidden className={on ? undefined : 'opacity-50'} />
      </button>
    </th>
  )
}

function FieldRow({
  row,
  open,
  reveal,
  maxHolds,
  onToggle,
}: {
  row: FieldMapRow
  open: boolean
  reveal: boolean
  maxHolds: number
  onToggle: () => void
}) {
  const ref = useRef<HTMLTableRowElement>(null)
  const detailRef = useRef<HTMLTableRowElement>(null)
  useEffect(() => {
    if (!open) return
    const tr = ref.current
    const scroller = tr?.closest<HTMLElement>('[data-table-scroll]')
    if (!tr || !scroller) return
    // Scroll the table itself, never the page: a field opened from a chart goes
    // to the top of the table (under its sticky header); one toggled in place
    // only scrolls far enough to show its detail.
    const box = scroller.getBoundingClientRect()
    const head = scroller.querySelector('thead')?.getBoundingClientRect().height ?? 0
    const at = tr.getBoundingClientRect()
    if (reveal) {
      scroller.scrollTop += at.top - box.top - head - 2
    } else {
      const detail = detailRef.current?.getBoundingClientRect() ?? at
      if (detail.bottom > box.bottom) scroller.scrollTop += Math.min(detail.bottom - box.bottom, at.top - box.top - head)
    }
    // The page (not the table) only moves if the table is off screen. jsdom has no layout, so guard it.
    scroller.scrollIntoView?.({ block: 'nearest' })
  }, [open, reveal])

  const blank = row.state === 'blank'
  const reason = rowReason(row)
  const holds = holdsBack(row)
  return (
    <>
      <tr ref={ref} data-field-row={row.field} className={clsx('border-t border-white/[0.05]', open && 'bg-white/[0.04]')}>
        <td className="px-2 py-1">
          <button
            type="button"
            aria-expanded={open}
            onClick={onToggle}
            title={row.label}
            className="flex max-w-full items-center gap-1 text-left text-stone-100 hover:text-amber-200 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-300"
          >
            <ChevronRight size={11} className={clsx('shrink-0 text-stone-600 transition-transform', open && 'rotate-90')} aria-hidden />
            <span className="truncate">{row.label}</span>
          </button>
        </td>
        <td className="truncate px-2 py-1 text-stone-500 max-md:hidden">{row.page}</td>
        <td className="px-2 py-1">
          <StatusChip bucket={bucketOf(row)} />
          <span className="sr-only">{blank ? 'Blank' : 'Populated'}</span>
        </td>
        <td className="truncate px-2 py-1 text-stone-400 max-lg:hidden" title={reason}>{reason}</td>
        <td className="px-2 py-1 max-lg:hidden">
          {holds > 0 ? (
            <span className="flex items-center gap-1.5 tabular-nums text-stone-300" title={`Its absence holds back ${holds} calculated ${holds === 1 ? 'field' : 'fields'}`}>
              <span aria-hidden className="h-1 w-12 overflow-hidden rounded-full bg-white/[0.07]">
                <span className="block h-full rounded-full bg-violet-400" style={{ width: `${(100 * holds) / maxHolds}%` }} />
              </span>
              {holds}
            </span>
          ) : (
            <span className="text-stone-700" aria-label="Holds back nothing">—</span>
          )}
        </td>
        <td className="px-2 py-1 max-lg:hidden">
          <span className="flex flex-wrap gap-1">
            {row.gate ? <Chip tone="amber">Gate</Chip> : null}
            {blank && row.impact?.creditScreenInput ? <Chip>Screen</Chip> : null}
            {row.assumed ? <Chip title={row.assumed.basis}>Default</Chip> : null}
            {row.ownership?.stamp ? (
              // The flags column is narrow: the short word here, the full stamp
              // and what it means in the title, the reason column and the detail.
              <Chip
                tone={row.ownership.awaitingDeskTerms ? 'amber' : 'stone'}
                title={row.ownership.note ? `${row.ownership.stamp}. ${row.ownership.note}` : row.ownership.stamp}
              >
                {row.ownership.seed === 'sponsor' ? 'Sponsor' : 'Desk-set'}
              </Chip>
            ) : null}
          </span>
        </td>
      </tr>
      {open ? (
        <tr ref={detailRef} className="bg-white/[0.02]">
          <td colSpan={6} className="px-3 py-2.5">
            <FieldDetail row={row} />
          </td>
        </tr>
      ) : null}
    </>
  )
}

export default FieldMapPage
